import {
  FormControl,
  FormLabel,
  HStack,
  Input,
  Text,
  VStack,
  FormErrorMessage,
  useToast,
} from '@chakra-ui/react';
import { type KeyboardEvent, useContext, useState } from 'react';
import { sendSignInLinkToEmail } from 'firebase/auth';
import { useNavigate } from 'react-router';
import {
  EMAIL_DELIVERY_TOAST_HINT,
  errorToastOptions,
  getAuthErrorMessage,
  isValidEmail,
  successToastOptions,
} from '@/helpers';
import type { FirebaseError } from 'firebase/app';
import { getAuthContinueOrigin } from '@/helpers/auth/authUrls';
import { FirebaseContext } from '@/session/FirebaseContext';
import {
  AuthHeading,
  AuthMutedTextButton,
  AuthPasswordField,
  AuthTextButton,
  EmailDeliveryHint,
  NeutralAuthButton,
  PrimaryAuthButton,
  useAuthTheme,
} from './auth-ui';
import { AuthDivider, GoogleButton, _handleEmailLogin } from './utils';
import { BaseAuthCard } from './base-card';
import { useSearchParams } from 'react-router-dom';

export function LogInCard(props: { triggerSignup: () => void; triggerForgot: () => void }) {
  const { auth } = useContext(FirebaseContext);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const toast = useToast();
  const { mutedText } = useAuthTheme();

  const [email, setEmail] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [pwVisible, setPWVisible] = useState<boolean>(false);
  const [loginAttempted, setLoginAttempted] = useState<boolean>(false);
  const [magicLinkMode, setMagicLinkMode] = useState<boolean>(false);
  const [signInEmailSent, setSignInEmailSent] = useState<boolean>(false);

  const handleEmailLogin = async () => {
    setLoginAttempted(true);
    if (!isValidEmail(email)) {
      return;
    }
    if (await _handleEmailLogin(auth, toast, email, password)) {
      const dest = searchParams.get('redirect_to') ?? '/profile';
      navigate(dest, { relative: 'path' });
    }
  };

  const handleMagicLink = () => {
    setLoginAttempted(true);
    if (!isValidEmail(email)) {
      return;
    }
    const origin = getAuthContinueOrigin();
    const dest = searchParams.get('redirect_to') ?? '/profile';
    sendSignInLinkToEmail(auth, email, {
      url: `${origin}/finish-sign-in?redirect_to=${encodeURIComponent(dest)}`,
      handleCodeInApp: true,
    })
      .then(() => {
        setSignInEmailSent(true);
        localStorage.setItem('emailForSignIn', email);
        toast(
          successToastOptions(
            'Login link sent',
            `10 Teams sent a sign-in link to your email. ${EMAIL_DELIVERY_TOAST_HINT}`,
          ),
        );
      })
      .catch((error: FirebaseError) => {
        toast(errorToastOptions('Sign in failed', getAuthErrorMessage(error)));
      });
  };

  const primaryDisabled =
    (loginAttempted && !isValidEmail(email)) ||
    signInEmailSent ||
    (!magicLinkMode && password.length === 0);

  return (
    <BaseAuthCard>
      <AuthHeading>Log in to your account</AuthHeading>
      <VStack align='stretch' spacing={4}>
        <FormControl isInvalid={loginAttempted && !isValidEmail(email)} isRequired>
          <FormLabel fontSize='sm' fontWeight='medium'>
            Email
          </FormLabel>
          <Input
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            type='email'
            autoComplete='email'
            size='md'
            borderRadius='md'
            onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
              if (e.key === 'Enter' && !signInEmailSent) {
                magicLinkMode ? handleMagicLink() : handleEmailLogin();
              }
            }}
          />
          <FormErrorMessage>A valid email is required.</FormErrorMessage>
        </FormControl>
        {!magicLinkMode && (
          <FormControl isRequired>
            <FormLabel fontSize='sm' fontWeight='medium'>
              Password
            </FormLabel>
            <AuthPasswordField
              value={password}
              onChange={setPassword}
              visible={pwVisible}
              onToggleVisible={() => setPWVisible(!pwVisible)}
              autoComplete='current-password'
              onEnter={handleEmailLogin}
            />
            <AuthMutedTextButton mt={1} onClick={props.triggerForgot} height='auto' minH={0} py={0}>
              Forgot your password?
            </AuthMutedTextButton>
          </FormControl>
        )}
        <PrimaryAuthButton
          isDisabled={primaryDisabled}
          onClick={magicLinkMode ? handleMagicLink : handleEmailLogin}
        >
          {magicLinkMode
            ? signInEmailSent
              ? 'Check your email to complete sign-in'
              : 'Send login link'
            : 'Log in'}
        </PrimaryAuthButton>
        {magicLinkMode && signInEmailSent && <EmailDeliveryHint />}
        <CenteredLinkToggle
          label={magicLinkMode ? 'Use password instead' : 'Email me a login link instead'}
          onClick={() => {
            setMagicLinkMode(!magicLinkMode);
            setSignInEmailSent(false);
          }}
        />
      </VStack>
      <AuthDivider />
      <HStack pb={1}>
        <GoogleButton auth={auth}>Log in with Google</GoogleButton>
      </HStack>
      <VStack align='stretch' spacing={3} pt={4}>
        <Text fontSize='sm' color={mutedText} textAlign='center'>
          Don&apos;t have an account yet?
        </Text>
        <NeutralAuthButton onClick={props.triggerSignup}>Sign up</NeutralAuthButton>
      </VStack>
    </BaseAuthCard>
  );
}

function CenteredLinkToggle(props: { label: string; onClick: () => void }) {
  return (
    <AuthTextButton width='full' onClick={props.onClick}>
      {props.label}
    </AuthTextButton>
  );
}
